import { ValueOpt } from "best-common-react";
import { StateCreator, create } from "zustand";
import { AddItemsPayload, EnterEditModePayload, EyeballTrainingType } from "../types/Eyeball";
import { sendIframeAddItemsModeMsg, sendIframeEditMsg, sendIframeResetEditModeMsg } from "../utils/EyeballUtil";

export const ALL_AUTHENTICATORS_FILTER_OPTION: ValueOpt<number> = {
  value: -1,
  label: "All Authenticators",
};

type State = {
  iframeUrl: string;
  gamePk: number | undefined;
  editingSessionAuthenticatorId: number;
  editMode: boolean;
  editItemId: number;
  eyeballSyncing: boolean;
  refreshReview: boolean;
  selectedAuthenticator: ValueOpt<number>;
  training?: EyeballTrainingType.REVIEW;
};

type Actions = {
  enterEditMode: (enterEditModePayload: EnterEditModePayload) => Promise<void>;
  exitEditMode: () => Promise<void>;
  enterAddItemsMode: (addItemsModePayload: AddItemsPayload) => Promise<void>;
  setGamePk: (gamePk: number) => void;
  setEyeballSyncing: (eyeballSyncing: boolean) => void;
  setRefreshReview: (refreshReview: boolean) => void;
  setSelectedAuthenticator: (authenticator: ValueOpt<number>) => void;
  setTraining: (training?: EyeballTrainingType.REVIEW) => void;
  resetSessionReviewStore: () => void;
};

type SessionReviewStore = State & Actions;

const initialState: State = {
  iframeUrl: window.MLBBest.envVariables.EYEBALL_APP_URL ?? "",
  gamePk: undefined,
  editingSessionAuthenticatorId: -1,
  editMode: false,
  editItemId: -1,
  eyeballSyncing: false,
  refreshReview: false,
  selectedAuthenticator: ALL_AUTHENTICATORS_FILTER_OPTION,
  training: undefined,
};

const createSessionReviewStore: StateCreator<SessionReviewStore, [], [], SessionReviewStore> = (set) => ({
  ...initialState,
  enterEditMode: async (enterEditModePayload: EnterEditModePayload) => {
    // Don't set state yet - wait for EDIT_STARTED confirmation from Eyeball
    // State will be updated when we receive EDIT_STARTED message in WrapperProvider
    await sendIframeEditMsg(enterEditModePayload);
  },
  exitEditMode: async () => {
    set({ editMode: false, editItemId: -1, eyeballSyncing: false, refreshReview: false }),
      await sendIframeResetEditModeMsg();
  },
  enterAddItemsMode: async (addItemsPayload: AddItemsPayload) => {
    // Don't set state yet - wait for EDIT_STARTED confirmation from Eyeball
    // State will be updated when we receive EDIT_STARTED message in WrapperProvider
    await sendIframeAddItemsModeMsg(addItemsPayload);
  },
  setGamePk: (gamePk: number | undefined) => set({ gamePk }),
  setEyeballSyncing: (eyeballSyncing: boolean) => set({ eyeballSyncing }),
  setRefreshReview: (refreshReview: boolean) => set({ refreshReview }),
  setSelectedAuthenticator: (authenticator: ValueOpt<number>) => set({ selectedAuthenticator: authenticator }),
  setTraining: (training?: EyeballTrainingType.REVIEW) => set({ training }),
  resetSessionReviewStore: () => set({ ...initialState }),
});

const useSessionReviewStore = create<SessionReviewStore>()((...a) => ({
  ...createSessionReviewStore(...a),
}));

export default useSessionReviewStore;
